/**
 * WebAudio voice builders: one scheduled note of a VoiceDef, and one filtered
 * noise grain. Every node a note creates is disconnected when the note ends,
 * so long sessions don't leak graph nodes.
 */
import { clamp01 } from './mix.ts';
import type { NoiseFilterType } from './sfxSpecs.ts';
import { noteGateS, noteLifetimeS, type VoiceDef } from './timbres.ts';

export interface NoteParams {
  hz: number;
  /** AudioContext start time (s). Times in the past are clamped to now. */
  time: number;
  /** Written length (s). */
  dur: number;
  /** 0–1. */
  velocity: number;
  /** Optional exponential pitch glide target, reached after glideS. */
  glideToHz?: number;
  glideS?: number;
}

/** Highest partial frequency we render, as a fraction of Nyquist (avoids aliasing harshness). */
const MAX_PARTIAL_OF_NYQUIST = 0.9;

function disconnectAll(nodes: readonly AudioNode[]): void {
  for (const n of nodes) {
    try {
      n.disconnect();
    } catch {
      // Already disconnected — nothing to do.
    }
  }
}

/**
 * Schedule one note. Returns the time at which its last node stops.
 * `click` noise (mallet/breath) needs `noise`; it is skipped otherwise.
 */
export function playVoice(
  ctx: BaseAudioContext,
  out: AudioNode,
  voice: VoiceDef,
  note: NoteParams,
  noise?: AudioBuffer,
  onEnded?: () => void,
): number {
  const t = Math.max(note.time, ctx.currentTime);
  const peak = voice.gain * clamp01(note.velocity);
  const nyquist = ctx.sampleRate / 2;
  if (!(peak > 0) || !(note.hz > 0) || note.hz >= nyquist * MAX_PARTIAL_OF_NYQUIST) {
    onEnded?.();
    return t;
  }
  const gate = t + noteGateS(voice, note.dur);
  const end = t + noteLifetimeS(voice, note.dur);
  const nodes: AudioNode[] = [];

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = voice.lowpassQ;
  const cutoff = Math.min(voice.lowpassHz, nyquist * MAX_PARTIAL_OF_NYQUIST);
  if (voice.filterFromHz) {
    filter.frequency.setValueAtTime(Math.min(voice.filterFromHz, nyquist * MAX_PARTIAL_OF_NYQUIST), t);
    filter.frequency.exponentialRampToValueAtTime(cutoff, t + (voice.filterSweepS ?? 0.1));
  } else {
    filter.frequency.setValueAtTime(cutoff, t);
  }
  filter.connect(out);
  nodes.push(filter);

  // Delayed vibrato shared by all partials (in cents, on each oscillator's detune).
  let vibratoDepth: GainNode | null = null;
  const sources: AudioScheduledSourceNode[] = [];
  if (voice.vibrato) {
    const lfo = ctx.createOscillator();
    lfo.frequency.value = voice.vibrato.hz;
    vibratoDepth = ctx.createGain();
    vibratoDepth.gain.setValueAtTime(0, t);
    vibratoDepth.gain.setValueAtTime(0, t + voice.vibrato.delayS);
    vibratoDepth.gain.linearRampToValueAtTime(voice.vibrato.cents, t + voice.vibrato.delayS + 0.25);
    lfo.connect(vibratoDepth);
    sources.push(lfo);
    nodes.push(lfo, vibratoDepth);
  }

  const twins = voice.detuneCents ? [-voice.detuneCents, voice.detuneCents] : [0];
  for (const p of voice.partials) {
    const f0 = note.hz * p.ratio;
    if (f0 >= nyquist * MAX_PARTIAL_OF_NYQUIST) continue;
    const level = (peak * p.gain) / twins.length;
    const decay = voice.decay * (p.decayScale ?? 1);
    for (const cents of twins) {
      const osc = ctx.createOscillator();
      osc.type = p.wave ?? 'sine';
      osc.frequency.setValueAtTime(f0, t);
      if (note.glideToHz && note.glideToHz > 0) {
        const f1 = Math.min(note.glideToHz * p.ratio, nyquist * MAX_PARTIAL_OF_NYQUIST);
        osc.frequency.exponentialRampToValueAtTime(f1, t + Math.max(0.005, note.glideS ?? note.dur));
      }
      osc.detune.setValueAtTime(cents, t);
      if (vibratoDepth) vibratoDepth.connect(osc.detune);

      const env = ctx.createGain();
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(level, t + voice.attack);
      env.gain.setTargetAtTime(level * voice.sustain, t + voice.attack, Math.max(0.005, decay));
      env.gain.setTargetAtTime(0, gate, Math.max(0.005, voice.release));

      osc.connect(env);
      env.connect(filter);
      sources.push(osc);
      nodes.push(osc, env);
    }
  }

  if (voice.click && noise) {
    const click = ctx.createBufferSource();
    click.buffer = noise;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = voice.click.hz;
    band.Q.value = 1;
    const env = ctx.createGain();
    const level = voice.click.gain * clamp01(note.velocity);
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(level, t + 0.002);
    env.gain.setTargetAtTime(0, t + 0.002, voice.click.durS / 3);
    click.connect(band);
    band.connect(env);
    env.connect(out);
    click.start(t);
    click.stop(t + voice.click.durS * 3 + 0.01);
    nodes.push(click, band, env);
  }

  for (const s of sources) {
    s.start(t);
    s.stop(end);
  }
  const sentinel = sources[sources.length - 1];
  if (sentinel) {
    sentinel.onended = () => {
      disconnectAll(nodes);
      onEnded?.();
    };
  } else {
    disconnectAll(nodes);
    onEnded?.();
  }
  return end;
}

export interface NoiseGrainParams {
  time: number;
  dur: number;
  gain: number;
  attack: number;
  filter: { type: NoiseFilterType; hz: number; toHz?: number; q: number };
  /** Start offset into the buffer (s), so repeated grains don't sound identical. */
  offsetS: number;
}

/** Schedule one filtered noise grain (crumble, puff, shaker, shimmer). */
export function playNoiseGrain(ctx: BaseAudioContext, out: AudioNode, buffer: AudioBuffer, p: NoiseGrainParams): void {
  const t = Math.max(p.time, ctx.currentTime);
  const level = clamp01(p.gain);
  if (!(level > 0) || !(p.dur > 0)) return;
  const nyquist = ctx.sampleRate / 2;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = p.filter.type;
  filter.Q.value = p.filter.q;
  filter.frequency.setValueAtTime(Math.min(p.filter.hz, nyquist * MAX_PARTIAL_OF_NYQUIST), t);
  if (p.filter.toHz) {
    filter.frequency.exponentialRampToValueAtTime(Math.min(p.filter.toHz, nyquist * MAX_PARTIAL_OF_NYQUIST), t + p.dur);
  }
  const attack = Math.min(p.attack, p.dur / 2);
  const tail = Math.max(0.005, (p.dur - attack) / 4);
  const env = ctx.createGain();
  env.gain.setValueAtTime(0, t);
  env.gain.linearRampToValueAtTime(level, t + attack);
  env.gain.setTargetAtTime(0, t + attack, tail);
  src.connect(filter);
  filter.connect(env);
  env.connect(out);
  const offset = buffer.duration > 0 ? p.offsetS % buffer.duration : 0;
  src.start(t, offset);
  src.stop(t + attack + tail * 7);
  src.onended = () => disconnectAll([src, filter, env]);
}
